import { useStore } from '@nanostores/react';
import { useCallback } from 'react';

import { DowngradeWarning } from './DowngradeWarning';
import { TokenReloadSettingsSection } from './TokenReload';
import { Button } from '~/components/ui/Button';
import { PricingPrice, PricingSettingSectionWrapper } from '~/components/ui/pricing/common';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { proPricingPlansStore } from '~/lib/stores/pricing/proPlans';
import { personalPricingSettingsStore } from '~/lib/stores/pricing/settings/proSettings';
import { userStore } from '~/lib/stores/user';
import { PRICING_URL } from '~/lib/urls';
import { AnalyticsAction, AnalyticsTrackEvent, PricingPageOpenedTrigger } from '~/types/analytics';
import type { SubscriptionInterval } from '~/types/subscription';
import type { UserData } from '~/types/user';

interface Props {
  user: UserData;
  closeSettingsDialog: () => void;
}

export const ProPlanPricingSettingsSection = ({ user, closeSettingsDialog }: Props) => {
  const nextPricing = useStore(personalPricingSettingsStore.nextHigherTierPricing);
  const isLoading = useStore(personalPricingSettingsStore.isLoading);

  const openPricingOverlay = useCallback(
    (action: 'see-all' | 'renew') => {
      closeSettingsDialog();

      if (window.location.pathname !== PRICING_URL) {
        pricingStore.openPricingOverlay(PricingPageOpenedTrigger.SettingsTokenTab);
      }

      if (action === 'renew') {
        proPricingPlansStore.selectActivePricing();
      }
    },
    [closeSettingsDialog],
  );

  const onSeeAllPlansClick = useCallback(() => {
    openPricingOverlay('see-all');
  }, []);

  const onRenewClick = useCallback(() => {
    openPricingOverlay('renew');
  }, []);

  const onUpgradeToNextTier = useCallback(() => {
    const currentUser = userStore.getUser();

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.ProPlanUpgradeClicked,
        properties: {
          // user context (who triggered the action)
          user_email: currentUser?.email,
          user_id: currentUser?.id,

          // plan context
          current_plan: user?.membership?.tier,
          target_tier: nextPricing?.tier,
          billing_interval: user?.membership?.subscription?.interval || 'monthly',
          current_subscription_status: user?.membership?.subscription?.status,

          // action context
          location: 'pro_settings',
        },
      },
    });

    personalPricingSettingsStore.upgradeToNextTier();
  }, []);

  if (!nextPricing) {
    return <TokenReloadSettingsSection />;
  }

  const interval: SubscriptionInterval = user?.membership?.subscription?.interval || 'monthly';
  const isCanceledSubscription = user?.membership?.subscription?.status === 'canceled';

  return (
    <>
      <PricingSettingSectionWrapper className="flex flex-col sm:grid sm:grid-cols-5 gap-2 sm:gap-6">
        <div className="flex flex-col items-start gap-2 sm:col-start-1 sm:col-end-4">
          <span className="font-semibold text-lg">Get more out of Pro</span>

          <span className="text-bolt-ds-textSecondary text-sm">
            Switch to the next tier and receive{' '}
            <span className="text-bolt-ds-textPrimary font-medium">{nextPricing.tokens} tokens per month.</span>
          </span>

          <Button variant="link-color" onClick={onSeeAllPlansClick}>
            See all available plans
          </Button>
        </div>

        <div className="flex flex-col gap-3 items-start sm:items-end justify-between mt-4 sm:mt-0 sm:col-start-4 sm:col-end-6">
          <PricingPrice label="per month" interval={interval} pricing={nextPricing} size="sm" />

          <Button onClick={onUpgradeToNextTier} loading={isLoading} disabled={isCanceledSubscription}>
            Upgrade plan
          </Button>
        </div>

        {isCanceledSubscription && <DowngradeWarning type="individual" onRenewClick={onRenewClick} />}
      </PricingSettingSectionWrapper>

      {interval === 'yearly' && <TokenReloadSettingsSection needCompact />}
    </>
  );
};
